/**
 * Regulamento, a pagina publica (specs 017 e 042), sem login. Mostra a versao ATUAL publicada no nucleo (ou a de
 * `?versao=N`) formatada pra ler, como a politica de privacidade do site (pedido do Vitor, 13/10: o link do checkout
 * abria o texto cru). `?documento=geral` mostra so aquele documento: e o link de cada caixa do checkout e do
 * "Regulamento desta compra". `?familia=EXAME` mostra o geral e o daquela familia. O PDF abre em outra aba.
 *
 * Duas portas pro arquivo da marca (`/marca/regulamentos/`), que e como era antes do nucleo:
 *  - o link antigo do aceite, com a versao exata (`?familia=EXAME&v=geral:2026-07-16,exame:2026-06`): ele aponta pra
 *    um arquivo que nunca muda, e e esse texto que quem comprou naquele tempo aceitou;
 *  - nada publicado ainda no nucleo (404): a versao vigente da marca, ate a primeira publicacao.
 */
import { Aviso, Botao, Carregando, IconeExterno, PaginaDeDocumento, TextoDeDocumento, formatarData, formatarDia, nomeDaVersao } from "@mtc/ui";
import { useQueries, useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router";
import { config, marca } from "../config";

export function PaginaRegulamento() {
  const [busca] = useSearchParams();
  // O link antigo traz a versao exata da marca: segue no arquivo da marca, que e o que foi aceito.
  if (busca.get("v")) return <RegulamentoDaMarca />;
  return <RegulamentoDoNucleo />;
}

type DocumentoPublico = { chave: string; titulo: string; familia: string | null; sha256: string; tipo: string; tamanho: number };
type GrupoPublico = { grupo: number; rotulo: string | null; situacao: "ATUAL" | "DESCONTINUADO"; publicado_em: string; documentos: DocumentoPublico[] };

const imprimir = <Botao variante="secundario" tamanho="pequeno" onClick={() => window.print()}>Imprimir ou salvar em PDF</Botao>;
const original = (href: string, rotulo = "Abrir o arquivo original") => (
  <a className="mtc-botao mtc-botao--secundario mtc-botao--pequeno" href={href} target="_blank" rel="noreferrer">{rotulo}<IconeExterno tamanho={16} /></a>
);

function RegulamentoDoNucleo() {
  const [busca] = useSearchParams();
  const api = config().API_URL.replace(/\/$/, "");
  const familia = busca.get("familia")?.toUpperCase() ?? null;
  const documento = busca.get("documento");
  const versao = Number(busca.get("versao")) || null;
  const grupo = useQuery({
    queryKey: ["regulamento-publico", versao],
    retry: false,
    queryFn: async (): Promise<GrupoPublico | null> => {
      const r = await fetch(`${api}/v1/publico/regulamentos${versao ? `/${versao}` : ""}`);
      if (r.status === 404) return null;
      if (!r.ok) throw new Error(String(r.status));
      return (await r.json()) as GrupoPublico;
    },
  });
  const g = grupo.data;
  const docs = (g?.documentos ?? []).filter((d) =>
    documento ? d.chave.toLowerCase() === documento.toLowerCase() : !familia || d.familia === null || d.familia.toUpperCase() === familia,
  );
  const textos = useQueries({
    queries: docs.map((d) => ({
      queryKey: ["regulamento-texto", g?.grupo, d.chave],
      enabled: Boolean(g) && !d.tipo.includes("pdf"),
      staleTime: Infinity,
      queryFn: async () => {
        const r = await fetch(`${api}/v1/publico/regulamentos/${g!.grupo}/${encodeURIComponent(d.chave)}/texto`);
        if (!r.ok) throw new Error(String(r.status));
        return ((await r.json()) as { texto: string }).texto;
      },
    })),
  });
  // Nada publicado no nucleo ainda (e nao pediram uma versao especifica): o arquivo vigente da marca.
  if (g === null && !versao) return <RegulamentoDaMarca />;
  const arquivo = (d: DocumentoPublico) => `${api}/v1/publico/regulamentos/${g!.grupo}/${encodeURIComponent(d.chave)}`;
  const so = docs.length === 1 && documento ? docs[0]! : null;
  const meta = g && (
    <>
      {nomeDaVersao(g.grupo, g.rotulo)}, publicada em {formatarData(g.publicado_em)}
      {g.situacao === "DESCONTINUADO" ? ". Não é mais a versão atual: vale pra quem comprou enquanto ela estava no ar." : "."}
    </>
  );
  const corpo = (d: DocumentoPublico, i: number): ReactNode =>
    d.tipo.includes("pdf") ? (
      // O PDF e o documento original (017, decisao 2). Dentro da pagina ele nao abre no celular: vai pra outra aba.
      <div className="mtc-pilha" style={{ gap: "var(--e-3)", justifyItems: "start" }}>
        <p className="mtc-texto-2" style={{ margin: 0 }}>Este documento é o PDF original. Abra pra ler ou baixar.</p>
        {original(arquivo(d), "Abrir o PDF")}
      </div>
    ) : textos[i]?.isLoading ? (
      <Carregando compacto texto="Carregando o documento" />
    ) : textos[i]?.isError ? (
      <Aviso tipo="erro">Não conseguimos abrir este documento agora. Use o “Abrir o arquivo original”.</Aviso>
    ) : (
      <TextoDeDocumento texto={textos[i]?.data ?? ""} titulo={d.titulo} />
    );
  return (
    <PaginaDeDocumento
      marca={marca()}
      rotulo="Regulamento"
      titulo={so?.titulo ?? "Regulamento"}
      meta={meta}
      acoes={g && docs.length > 0 && !so?.tipo.includes("pdf") && <>{so && original(arquivo(so))}{imprimir}</>}
    >
      {grupo.isLoading ? (
        <Carregando compacto texto="Carregando o regulamento" />
      ) : grupo.isError || g === null ? (
        <Aviso tipo="erro">O regulamento não está disponível agora. Fale com o suporte.</Aviso>
      ) : !docs.length ? (
        <Aviso tipo="erro">{documento ? "Este documento não existe nesta versão do regulamento." : "Esta versão não tem regulamento pra essa família."}</Aviso>
      ) : so ? (
        <div data-regulamento={so.chave}>{corpo(so, 0)}</div>
      ) : (
        docs.map((d, i) => (
          <section key={d.chave} className="mtc-folha__documento" data-regulamento={d.chave}>
            <h2 className="mtc-folha__titulo-doc">{d.titulo}</h2>
            {corpo(d, i)}
            {!d.tipo.includes("pdf") && <div className="mtc-folha__acoes">{original(arquivo(d))}</div>}
          </section>
        ))
      )}
    </PaginaDeDocumento>
  );
}

// ---------------------------------------------------------------- o arquivo da marca (antes do nucleo)

type Indice = { documentos: Record<string, { titulo: string; atual: string; versoes: Record<string, { arquivo: string }> }> };

/** "geral:2026-07-16,exame:2026-06" -> { geral: "2026-07-16", EXAME: "2026-06" } */
function versoesPedidas(v: string | null): Record<string, string> {
  return Object.fromEntries(
    (v ?? "")
      .split(",")
      .map((p) => p.split(":"))
      .filter((p): p is [string, string] => p.length === 2 && Boolean(p[0]) && Boolean(p[1]))
      .map(([d, ver]) => [d.toLowerCase() === "geral" ? "geral" : d.toUpperCase(), ver]),
  );
}

const rotuloDaVersao = (v: string) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? `atualizado em ${formatarDia(v)}` : /^\d{4}-\d{2}$/.test(v) ? `versão de ${v.slice(5)}/${v.slice(0, 4)}` : `versão ${v}`);

function RegulamentoDaMarca() {
  const [busca] = useSearchParams();
  const familia = busca.get("familia")?.toUpperCase() ?? null;
  const pedidas = versoesPedidas(busca.get("v"));
  const indice = useQuery({ queryKey: ["regulamentos"], queryFn: async () => { const r = await fetch("/marca/regulamentos/indice.json", { cache: "no-cache" }); if (!r.ok) throw new Error(String(r.status)); return (await r.json()) as Indice; }, retry: false });
  const docs = indice.data
    ? ["geral", ...(familia ? [familia] : [])]
        .map((chave) => ({ chave, doc: indice.data.documentos[chave] }))
        .filter((x): x is { chave: string; doc: Indice["documentos"][string] } => Boolean(x.doc))
        .map(({ chave, doc }) => {
          const versao = pedidas[chave] ?? doc.atual;
          return { chave, titulo: doc.titulo, versao, arquivo: doc.versoes[versao]?.arquivo ?? null };
        })
    : [];
  const textos = useQueries({ queries: docs.map((d) => ({ queryKey: ["regulamento", d.arquivo], enabled: Boolean(d.arquivo), queryFn: async () => { const r = await fetch(`/marca/regulamentos/${d.arquivo}`); if (!r.ok) throw new Error(String(r.status)); return r.text(); } })) });
  return (
    <PaginaDeDocumento marca={marca()} rotulo="Regulamento" titulo="Regulamento" acoes={docs.length > 0 && imprimir}>
      {indice.isLoading ? <Carregando compacto texto="Carregando o regulamento" /> : indice.isError || !docs.length ? <Aviso tipo="erro">O regulamento não está disponível agora. Fale com o suporte.</Aviso> : null}
      {docs.map((d, i) => (
        <section key={d.chave} className="mtc-folha__documento" data-regulamento={d.chave}>
          <div>
            <h2 className="mtc-folha__titulo-doc">{d.titulo}</h2>
            <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{rotuloDaVersao(d.versao)}</span>
          </div>
          {!d.arquivo ? (
            <Aviso tipo="erro">Esta versão do regulamento não existe.</Aviso>
          ) : textos[i]?.isLoading ? (
            <Carregando compacto />
          ) : (
            <TextoDeDocumento texto={textos[i]?.data ?? ""} titulo={d.titulo} />
          )}
        </section>
      ))}
    </PaginaDeDocumento>
  );
}
